import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { authFetch } from "../../utils/authFetch";
import { BASE_URL } from "../../utils/config";
import { useAuth } from "../../context/AuthContext";
import { useFlow } from "../../context/FlowContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import eyeIcon from "../../images/eye.svg";
import eyeOffIcon from "../../images/eye-closed.svg";
import Autocomplete from "react-google-autocomplete";
import { useTranslation, i18n } from "react-i18next";
import LanguageDropdown from "../../components/LanguageDropdown";

const api_Key =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const Login = ({ setHideFooter }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    isAuthenticated,
    currentUser,
    login,
    logout: authLogout,
    fetchAndUpdateUser,
  } = useAuth();
  const { setFlow } = useFlow();
  const initialIsLogin = searchParams.get("mode") === "login";
  const [isLogin, setIsLogin] = useState(false);
  const [userType, setUserType] = useState("private");
  const [isLoading, setIsLoading] = useState(false);
  const [displayRegistrationMessage, setDisplayRegistrationMessage] =
    useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const [formData, setFormData] = useState(() => ({
    firstName: "",
    lastName: "",
    companyName: "",
    street: "",
    address: "",
    streetNumber: "",
    country: "",
    zipCode: "",
    city: "",
    vatNumber: "",
    language: i18n.language || "en",
    password: "",
    alertPreference: ["email", "app"],
    termsAccepted: false,
    smsVerificationCode: "",
    email: "",
    loginPassword: "",
    countryCode: "CH",
    mobile: "",
  }));
  // Keep form language in sync with i18n language
  useEffect(() => {
    // Update form data when i18n language changes
    const handleLanguageChange = (lng) => {
      const i18nToFormLanguageMap = {
        en: "en",
        de: "de",
        fr: "fr",
        it: "it",
      };
      const formLanguage = i18nToFormLanguageMap[lng] || "en";
      
      setFormData(prev => ({
        ...prev,
        language: formLanguage
      }));
    };

    // Set initial language from i18n
    handleLanguageChange(i18n.language);

    // Subscribe to language changes
    i18n.on('languageChanged', handleLanguageChange);

    // Cleanup
    return () => {
      // Polling disabled to prevent property changes during animation
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    setHideFooter(true);
    return () => {
      // Polling disabled to prevent property changes during animation
      setHideFooter(false);
    };
  }, [setHideFooter]);

  const requestedFlowType = searchParams.get("flowType");
  const isDemandFlowRequested = requestedFlowType === "demand";

  useEffect(() => {
    const currentMode = searchParams.get("mode");
    setIsLogin(currentMode === "login");
    if (isAuthenticated && currentUser) {
      if (currentUser.userType === requestedFlowType) {
        if (currentUser.isSubscribed) {
          toast.info(
            `Welcome! Redirecting you to your ${
              currentUser.userType === "offer" ? "properties" : "searches"
            }.`,
            { autoClose: 2000 }
          );
          setTimeout(() => {
            navigate(
              currentUser.userType === "offer" ? "/MyProperties" : "/MySearch"
            );
          }, 100);
        } else {
          setTimeout(() => {
            navigate("/subscription");
          }, 100);
        }
      } else if (
        requestedFlowType &&
        currentUser.userType !== requestedFlowType
      ) {
        console.warn("Cross-flow attempt detected for logged-in user!");

        authLogout();
        setFlow(null);
        setIsLogin(false);
        localStorage.removeItem("propertyId");
        return;
      }
    }

    const nameFromUrl = searchParams.get("name");
    const mobileFromUrl = searchParams.get("mobile");
    const emailFromUrl = searchParams.get("email");

    if (
      !isLogin &&
      requestedFlowType === "offer" &&
      (nameFromUrl || mobileFromUrl || emailFromUrl)
    ) {
      setFormData((prev) => ({
        ...prev,
        firstName: nameFromUrl || prev.firstName,
        mobile: mobileFromUrl || prev.mobile,
        email: emailFromUrl || prev.email,
      }));
      console.log("Pre-filled registration form from OfferProperty data.");
    }

    if (isDemandFlowRequested) {
      setUserType("private");
      setFormData((prev) => ({
        ...prev,
        companyName: "",
        vatNumber: "",
      }));
    } else {
      setUserType("private");
    }
  }, [
    searchParams,
    isLogin,
    isAuthenticated,
    currentUser,
    requestedFlowType,
    authLogout,
    setFlow,
    navigate,
    setUserType,
  ]);

  // Set default language based on current i18n language
  useEffect(() => {
    const i18nToFormLanguageMap = {
      en: "en",
      de: "de",
      fr: "fr",
      it: "it",
    };

    const currentLanguage = i18nToFormLanguageMap[i18n.language] || "en";

    setFormData((prev) => ({
      ...prev,
      language: currentLanguage,
    }));
  }, [i18n.language]);

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 8) {
      errors.push(t("registration.passwordErrors.minLength"));
    }
    if (!/[A-Z]/.test(password)) {
      errors.push(t("registration.passwordErrors.uppercase"));
    }
    if (!/[0-9]/.test(password)) {
      errors.push(t("registration.passwordErrors.number"));
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push(t("registration.passwordErrors.special"));
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle language change
    if (name === "language") {
      i18n.changeLanguage(value);
      setFormData(prev => ({
        ...prev,
        language: value
      }));
      return;
    }

    // Format phone number with auto + prefix and Swiss pattern (+41791235784)
    let finalValue = value;
    if (name === "mobile") {
      // Remove all non-digit characters except +
      let cleaned = value.replace(/[^\d+]/g, "");
      
      // Auto-add + at the beginning if not present
      if (!cleaned.startsWith("+")) {
        cleaned = "+" + cleaned.replace(/\+/g, "");
      }
      
      // Remove any extra + signs
      cleaned = "+" + cleaned.replace(/\+/g, "");
      
      // Limit to 13 characters (+ followed by 12 digits for Swiss numbers)
      if (cleaned.length > 13) {
        cleaned = cleaned.substring(0, 13);
      }
      
      // Only allow digits after +
      const digitsOnly = cleaned.substring(1).replace(/\D/g, "");
      finalValue = "+" + digitsOnly;
    }

    // Validate password on change
    if (name === "password" && finalValue) {
      const errors = validatePassword(finalValue);
      setPasswordErrors(errors);
    } else if (name === "password" && !finalValue) {
      setPasswordErrors([]);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : finalValue,
    }));
  };

  const handleUserTypeChange = (type) => {
    setUserType(type);
    if (type === "private") {
      setFormData((prev) => ({
        ...prev,
        companyName: "",
        vatNumber: "",
      }));
    }
  };

  const handleCloseButtonClick = () => {
    if (!isLogin) {
      // If it's the registration form
      setShowCloseConfirmation(true); // Show confirmation
    } else {
      // If it's the login form
      navigate("/"); // Directly navigate to home
    }
  };

  // Function to confirm closing the registration form and redirect
  const confirmClose = () => {
    setShowCloseConfirmation(false); // Hide confirmation
    navigate("/"); // Navigate to home
  };

  // Function to cancel closing the registration form
  const cancelClose = () => {
    setShowCloseConfirmation(false); // Hide confirmation
  };

  const handlePlaceSelected = async (place, inputRef) => {
    const addressComponents = place?.address_components || [];

    const getComponent = (type) => {
      const component = addressComponents.find((comp) =>
        comp.types.includes(type)
      );
      return component ? component.long_name : "";
    };

    const streetName = getComponent("route");
    const streetNumber = getComponent("street_number");
    const fullAddress = streetNumber ? `${streetName} ${streetNumber}` : streetName;
    const country = getComponent("country");
    const city =
      getComponent("locality") ||
      getComponent("administrative_area_level_2") ||
      getComponent("administrative_area_level_1");
    const zipcode = getComponent("postal_code");

    // Update the input field to show only street address
    if (inputRef) {
      inputRef.value = fullAddress;
    }

    setFormData((prev) => ({
      ...prev,
      address: fullAddress,
      streetNumber: streetNumber,
      country: country,
      city: city,
      zipCode: zipcode,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLogin) {
      if (!formData.email || !formData.loginPassword) {
        toast.info("Please enter both email and password for login.");
        setIsLoading(false); // Ensure it's false if it somehow got set true
        return; // Stop execution
      }
      setIsLoading(true);
      const loginData = {
        email: formData.email,
        password: formData.loginPassword,
      };

      try {
        const response = await authFetch(`${BASE_URL}/users/login`, {
          method: "POST",
          body: JSON.stringify(loginData),
        });

        const data = await response.json();
        console.log("response", response);
        console.log("data", data);

        if (data.token && response.ok) {
          console.log("Login successful:", data);
          localStorage.setItem("token", data.token);
          const userToStore = {
            ...data.data,
            email: formData.email,
          };

          localStorage.setItem("user", JSON.stringify(userToStore));

          login(userToStore);

          setFlow(data.data.userType);

          // Check if phone verification is required
          if (
            data.data.isPhoneVerified == false ||
            data.data.isPhoneVerified === null
          ) {
            // Show modal popup for phone verification
            const showVerificationModal = window.confirm(
              "Please verify your phone number to continue. Click OK to proceed with verification."
            );
            if (showVerificationModal) {
              navigate("/auth/otp-verification?from=login");
            } else {
              // User cancelled, stay on login page
              setIsLoading(false);
              return;
            }
          } else {
            toast.success("Login Successful!", {
              autoClose: 3000,
              position: "bottom-right",
            });
            setTimeout(() => {
              console.log("in set timeout");

              // *** This is the crucial change for the login logic ***
              if (data.data.userType === "demand") {
                navigate("/");
              } else if (data.data.userType === "offer") {
                // Directly use data.data.isSubscribed from the fresh login response
                if (data.data.isSubscribed) {
                  navigate("/MyProperties");
                } else {
                  navigate("/subscription");
                }
              } else {
                navigate("/");
              }
            }, 1000);
          }
        } else {
          console.error("Login failed:", data.message || data.error || data);
          const errorMessage = data.message || data.error || "Invalid email or password. Please try again.";
          toast.error(errorMessage, { autoClose: 4000 });
        }
      } catch (error) {
        console.error("Error during login:", error);
        toast.error("Network error. Please check your connection and try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      // Registration Logic
      const languageMap = {
        en: "EN",
        de: "DE",
        fr: "FR",
        it: "IT",
      };

      const propertyIdFromLocalStorage = localStorage.getItem("propertyId");
      
      // Log propertyId status with context
      if (isDemandFlowRequested) {
        if (propertyIdFromLocalStorage) {
          console.log(
            "DEBUG: propertyId found for demand registration:",
            propertyIdFromLocalStorage
          );
        } else {
          console.warn(
            "DEBUG: propertyId is missing for demand registration. This is required."
          );
        }
      } else {
        // For offer flow, propertyId may be null (user creates property after registration)
        console.log(
          "DEBUG: Registration for offer flow. propertyId:",
          propertyIdFromLocalStorage || "null (will be created after registration)"
        );
      }
      
      if (!formData.termsAccepted) {
        // Use a toast notification instead of alert()
        toast.error("You must accept the Terms and Conditions to proceed.");
        setIsLoading(false);
        return; // Stop the submission/navigation
      }

      // Validate password before submission
      const passwordValidationErrors = validatePassword(formData.password);
      if (passwordValidationErrors.length > 0) {
        setPasswordErrors(passwordValidationErrors);
        toast.error(t("registration.passwordErrors.fixErrors"));
        setIsLoading(false);
        return;
      }

      if (isDemandFlowRequested && !propertyIdFromLocalStorage) {
        toast.error(
          "Property ID is missing for demand registration. Please ensure you've completed the property search step."
        );
        console.error(
          "Demand registration failed: propertyId is null or undefined."
        );
        setIsLoading(false);
        return;
      }

      // Frontend validation for required fields
      const validationErrors = [];
      if (!formData.firstName || !formData.firstName.trim()) {
        validationErrors.push("First name is required");
      }
      if (!formData.lastName || !formData.lastName.trim()) {
        validationErrors.push("Last name is required");
      }
      if (!formData.address || !formData.address.trim()) {
        validationErrors.push("Address is required");
      }
      if (!formData.country || !formData.country.trim()) {
        validationErrors.push("Country is required");
      }
      if (!formData.city || !formData.city.trim()) {
        validationErrors.push("City is required");
      }
      if (!formData.zipCode || !formData.zipCode.trim()) {
        validationErrors.push("ZIP code is required");
      }
      if (!formData.mobile || !formData.mobile.trim()) {
        validationErrors.push("Mobile number is required");
      }
      if (!formData.email || !formData.email.trim()) {
        validationErrors.push("Email is required");
      }
      if (userType === "company" && !isDemandFlowRequested) {
        if (!formData.companyName || !formData.companyName.trim()) {
          validationErrors.push("Company name is required");
        }
        if (!formData.vatNumber || !formData.vatNumber.trim()) {
          validationErrors.push("VAT number is required");
        }
      }

      if (validationErrors.length > 0) {
        toast.error(validationErrors[0] || "Please fill in all required fields");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      // Get criteria from localStorage if available (set in OfferProperty step 2)
      let propertyCriteria = null;
      try {
        const criteriaStr = localStorage.getItem("propertyCriteria");
        if (criteriaStr) {
          propertyCriteria = JSON.parse(criteriaStr);
        }
      } catch (e) {
        console.error("Failed to parse propertyCriteria:", e);
      }

      const registrationPayload = {
        propertyId: propertyIdFromLocalStorage,
        name: formData.firstName?.trim() || "",
        surname: formData.lastName?.trim() || "",
        address: formData.address?.trim() || "",
        streetNumber: formData.streetNumber?.trim() || "",
        country: formData.country?.trim() || "",
        city: formData.city?.trim() || "",
        zipCode: formData.zipCode?.trim() || "",
        countryCode: formData.countryCode || "CH",
        mobile: formData.mobile?.trim() || "",
        email: formData.email?.trim() || "",
        password: formData.password || "",
        language: languageMap[formData.language] || "EN",
        alertsEmail: formData.alertPreference.includes("email"),
        alertsSound: formData.alertPreference.includes("sms"),
        alertsWeb: formData.alertPreference.includes("web"),
        alertsApp: formData.alertPreference.includes("app"),
        agreedTerms: formData.termsAccepted === true,
        smsCode: formData.smsVerificationCode || "",
        vatId:
          !isDemandFlowRequested && userType === "company"
            ? formData.vatNumber?.trim() || ""
            : "",
        companyName:
          !isDemandFlowRequested && userType === "company"
            ? formData.companyName?.trim() || ""
            : "",
        isOffer: requestedFlowType === "offer",
        isPrivate: isDemandFlowRequested ? true : userType === "private",
        userType: requestedFlowType === "demand" ? "demand" : "offer",
        ...(propertyCriteria && propertyIdFromLocalStorage ? {
          PropertyCriteria: { criteria: propertyCriteria }
        } : {}),
      };

      console.log("Registration Payload:", registrationPayload);
      try {
        console.log("📤 Final Registration Payload:", registrationPayload);
        console.log("✅ FormData Snapshot:", formData);
        console.log(
          "🟡 JSON Body Being Sent:",
          JSON.stringify(registrationPayload)
        );

        const response = await authFetch(`${BASE_URL}/users/register`, {
          method: "POST",

          body: JSON.stringify(registrationPayload),
        });

        const data = await response.json();
        if (response.ok && data.token && data.data) {
          console.log("Registration successful:", data);
          localStorage.setItem("token", data.token);
          // Store the user data in localStorage for registration
          localStorage.setItem("user", JSON.stringify(data.data));
          //login(data.data);
          setFlow(data?.data?.userType);
          toast.success("Registration Successful!", { autoClose: 3000 });

          setTimeout(() => {
            // Redirect to OTP verification with user contact info
            const otpParams = new URLSearchParams({
              email: formData.email,
              phone: formData.mobile,
            });
            // navigate("/auth/otp-verification?from=login");
            console.log("params sending", otpParams.toString());
            navigate(
              `/auth/otp-verification?from=register&${otpParams.toString()}`
            );
          }, 1000);
        } else {
          if (data.error === "User already exists.") {
            toast.error("User already exists for this email.");
          } else {
            toast.error(
              data.message ||
                "Registration failed. Please check your input and try again."
            );
          }
          console.error(
            "Registration failed:",
            data.message || data.error || data
          );
        }
      } catch (error) {
        toast.error(
          "Registration error. Please check your internet connection and try again."
        );
        console.error("Error during registration:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // const handleOverlayClick = (e) => {
  //   if (e.target === e.currentTarget) {
  //     window.history.back();
  //   }
  // };

  const handlePasswordReset = (e) => {
    e.preventDefault();
    navigate("/auth/forgot-password");
  };

  //  (inside Login component)

  const toggleAuthMode = () => {
    const newIsLogin = !isLogin; // <<< ADD THIS LINE
    setIsLogin(newIsLogin);
    setDisplayRegistrationMessage("");
    setFormData((prev) => ({
      ...prev,
      loginPassword: "",
      password: "",
    }));

    // Update the URL to reflect the new mode
    const currentSearchParams = new URLSearchParams(searchParams.toString());
    if (newIsLogin) {
      currentSearchParams.set("mode", "login");
    } else {
      currentSearchParams.delete("mode"); // Remove mode if going back to register
    }
    navigate({ search: currentSearchParams.toString() }, { replace: true }); // Use replace to avoid extra history entries
  };

  const RadioButton = ({ name, value, checked, onChange, label }) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        marginRight: "15px",
      }}
    >
      <div
        style={{
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          border: "2px solid #29B185",
          marginRight: "8px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {checked && (
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: "#29B185",
            }}
          />
        )}
      </div>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ display: "none" }}
      />
      {label}
    </label>
  );
  const Checkbox2 = ({ name, value, checked, onChange, label }) => (
    <div style={{ 
      display: "flex", 
      alignItems: "center", 
      margin: '4px 0',
      minHeight: '24px'
    }}>
      <div style={{
        position: 'relative',
        width: '18px',
        height: '18px',
        minWidth: '18px',
        border: '1px solid #29B185',
        borderRadius: '3px',
        backgroundColor: checked ? '#29B185' : 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease-in-out',
        marginRight: '12px',
        flexShrink: 0,
        '&:hover': {
          boxShadow: '0 0 0 2px rgba(41, 177, 133, 0.2)'
        }
      }}>
        <input
          type="checkbox"
          name={name}
          id={`${name}-${value}`}
          value={value}
          checked={checked}
          onChange={onChange}
          style={{
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            margin: 0,
            cursor: 'pointer'
          }}
          aria-label={typeof label === 'string' ? label : ''}
        />
        {checked && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: '12px',
              height: '12px',
              flexShrink: 0
            }}
            aria-hidden="true"
          >
            <path
              d="M20 6L9 17L4 12"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <label
        htmlFor={`${name}-${value}`}
        style={{
          fontSize: "14px",
          lineHeight: "1.4",
          color: "#333",
          cursor: "pointer",
          margin: 0,
          padding: 0,
          userSelect: 'none',
          fontWeight: 400
        }}
      >
        {label}
      </label>
    </div>
  );

  const Checkbox = ({ name, checked, onChange, label }) => (
    <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
      <div
        style={{
          width: "14px",
          height: "14px",
          border: "2px solid #29B185",
          marginRight: "8px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {checked && (
          <span
            style={{
              color: "#29B185",
              fontSize: "12px",
              lineHeight: "1",
              fontWeight: "bold",
            }}
          >
            &#10003;
          </span>
        )}
      </div>
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        style={{ display: "none" }}
      />
      {label} {/* This is where your JSX label will be rendered */}
    </label>
  );

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "60px 50px 80px",
          borderRadius: "8px",
          width: "350px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          cursor: "default",
          overflowY: "auto",
          maxHeight: "90vh",
          position: "relative",
          marginTop: "40px",
          paddingTop: "40px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ... rest of the code remains the same ... */}
        {/* Language Selector on Left */}
        <div style={{
          position: 'absolute',
          top: '15px',
          left: '15px',
          zIndex: 1000
        }}>
          <LanguageDropdown />
        </div>
        
        {/* Close Button on Right */}
        <button
          onClick={handleCloseButtonClick}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: '#29B185',
            border: 'none',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '20px',
            cursor: 'pointer',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.3s ease',
            padding: 0
          }}
          aria-label={t("closeModal")}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#249f76';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#29B185';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          &times;
        </button>
        {showCloseConfirmation && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
              zIndex: 1001,
              textAlign: "center",
              width: "80%",
              maxWidth: "300px",
            }}
          >
            <h3 style={{ color: "#DC3545", marginBottom: "20px" }}>
              {t("confirmation.title")}
            </h3>
            <p style={{ marginBottom: "25px", fontSize: "15px" }}>
              {t("confirmation.message")}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                gap: "15px",
              }}
            >
              <button
                onClick={confirmClose}
                style={{
                  backgroundColor: "#DC3545",
                  color: "white",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "16px",
                  flex: 1,
                }}
              >
                {t("confirmation.yesCancel")}
              </button>
              <button
                onClick={cancelClose}
                style={{
                  backgroundColor: "#29B185",
                  color: "white",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "16px",
                  flex: 1,
                }}
              >
                {t("confirmation.noContinue")}
              </button>
            </div>
          </div>
        )}

        {displayRegistrationMessage && (
          <div
            style={{
              padding: "15px",
              backgroundColor: "#ffe0b2",
              color: "#e65100",
              border: "1px solid #ff9800",
              borderRadius: "8px",
              marginBottom: "20px",
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {t(displayRegistrationMessage)}
          </div>
        )}

        {!isAuthenticated ||
        (isAuthenticated &&
          currentUser &&
          currentUser.userType !== requestedFlowType) ? (
          isLogin ? (
            <div>
              {/* Note below close button and above login title */}
              <div
                style={{
                  marginTop: "20px",
                  marginBottom: "20px",
                  padding: "12px 15px",
                  backgroundColor: "#f0f7f5",
                  border: "1px solid #29B185",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontSize: "14px",
                  color: "#333",
                  lineHeight: "1.5",
                }}
              >
                {t("login_note")}
              </div>
              <h1 style={{ color: "#29B185", textAlign: "center" }}>
                {t("login")}
              </h1>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: "15px" }}>
                  <div
                    style={{
                      color: "#29B185",
                      marginBottom: "10px",
                      marginTop: "20px",
                      fontSize: "20px",
                      fontWeight: "700",
                    }}
                  >
                    {t("email")}
                  </div>
                  <input
                    type="email"
                    name="email"
                    placeholder={t("email")}
                    value={formData.email}
                    onChange={handleChange}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "none",

                      backgroundColor: "rgba(41, 177, 133, 0.25)",
                    }}
                    required
                  />
                </div>
                <div style={{ marginBottom: "5px" }}>
                  <h3
                    style={{
                      color: "#29B185",
                      marginBottom: "10px",
                      marginTop: "20px",
                    }}
                  >
                    {t("password")}
                  </h3>

                  <div style={{ position: "relative", marginBottom: "5px" }}>
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      name="loginPassword"
                      placeholder={t("password")}
                      value={formData.loginPassword}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                    />
                    {formData.loginPassword && (
                      <img
                        src={showLoginPassword ? eyeOffIcon : eyeIcon}
                        alt={t("togglePassword")}
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          opacity: 0.6,
                        }}
                      />
                    )}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "small",
                    marginBottom: "20px",
                    textAlign: "center",
                    marginTop: "20px",
                  }}
                >
                  {/* <a
                    href="#change"
                    onClick={(e) => e.preventDefault()}
                    style={{ marginRight: "10px", color: "black" }}
                  >
                    {t("changePassword")}
                  </a> */}
                  <a
                    href="#reset"
                    onClick={handlePasswordReset}
                    style={{ color: "black" }}
                  >
                    {t("signIn.resetPassword")}
                  </a>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "20px",
                    fontSize: "small",
                  }}
                >
                  <p>
                    {t("noAccount")}
                    <button
                      type="button"
                      onClick={toggleAuthMode}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#29B185",
                        cursor: "pointer",

                        textDecoration: "underline",
                        padding: 0,
                      }}
                    >
                      {t("signUp")}
                    </button>
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    backgroundColor: "#29B185",
                    color: "white",
                    marginLeft: "25px",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    fontSize: "22px",
                    width: "90%",
                    marginBottom: "15px",
                  }}
                >
                  {isLoading ? t("loggingIn") : t("login")}
                </button>
              </form>
            </div>
          ) : (
            // --- Registration Form (when !isLogin) ---
            <div
              style={
                showCloseConfirmation
                  ? { filter: "blur(2px)", pointerEvents: "none" }
                  : {}
              }
            >
              {requestedFlowType === "demand" ? (
                <h1
                  style={{
                    color: "#29B185",
                    textAlign: "center",
                    marginBottom: "20px",
                  }}
                >
                  {t("registration.freeTitle")}
                </h1>
              ) : (
                <h1
                  style={{
                    color: "#29B185",
                    textAlign: "center",
                    marginBottom: "20px",
                  }}
                >
                  {t("registration.title")}
                </h1>
              )}
              {!isDemandFlowRequested && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "20px",
                    marginBottom: "20px",
                  }}
                >
                  <RadioButton
                    name="userType"
                    value="private"
                    checked={userType === "private"}
                    onChange={() => handleUserTypeChange("private")}
                    label={t("registration.private")}
                  />
                  <RadioButton
                    name="userType"
                    value="company"
                    checked={userType === "company"}
                    onChange={() => handleUserTypeChange("company")}
                    label={t("registration.company")}
                  />
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div
                  style={{ display: "flex", gap: "20px", marginBottom: "5px" }}
                >
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="firstName"
                      placeholder={t("registration.placeholderName")}
                      value={formData.firstName}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="lastName"
                      placeholder={t("registration.placeholderSurname")}
                      value={formData.lastName}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                    />
                  </div>
                </div>
                {userType === "company" && (
                  <div style={{ marginBottom: "15px" }}>
                    <input
                      type="text"
                      name="companyName"
                      placeholder={t("registration.placeholderCompanyName")}
                      value={formData.companyName}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                    />
                  </div>
                )}
                <div
                  style={{ display: "flex", gap: "20px", marginBottom: "5px" }}
                >
                  <div style={{ flex: 5 }}>
                    <Autocomplete
                      apiKey={api_Key}
                      onPlaceSelected={handlePlaceSelected}
                      options={{
                        types: ["address"],
                        componentRestrictions: { country: ["ch"] },
                      }}
                      className="form-input-style"
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      placeholder={t("registration.placeholderStreet")}
                    />
                    <input
                      type="hidden"
                      name="address"
                      value={formData.address}
                      required
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="streetNumber"
                      placeholder={t("registration.placeholderNr")}
                      value={formData.streetNumber}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                    />
                  </div>
                </div>
                <div
                  style={{ display: "flex", gap: "20px", marginBottom: "5px" }}
                >
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="country"
                      placeholder={t("registration.placeholderCountry")}
                      value={formData.country}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                      disabled
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="zipCode"
                      placeholder={t("registration.placeholderZIP")}
                      value={formData.zipCode}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                      // disabled
                    />
                  </div>
                  <div style={{ flex: 4 }}>
                    <input
                      type="text"
                      name="city"
                      placeholder={t("registration.placeholderCity")}
                      value={formData.city}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                      disabled
                    />
                  </div>
                </div>
                <div
                  style={{ display: "flex", gap: "20px", marginBottom: "10px" }}
                >
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="countryCode"
                      placeholder="+41"
                      value={formData.countryCode}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                    />
                  </div>
                  <div style={{ flex: 2 }}>
                    <input
                      type="tel"
                      name="mobile"
                      placeholder={t("registration.placeholderMobile") || "+41791235784"}
                      value={formData.mobile}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                    />
                  </div>
                  <div style={{ flex: 3 }}>
                    <input
                      type="email"
                      name="email"
                      placeholder={t("registration.placeholderEmail")}
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                    />
                  </div>
                </div>
                {userType === "company" && (
                  <div style={{ marginBottom: "15px" }}>
                    <input
                      type="text"
                      name="vatNumber"
                      placeholder={t("registration.placeholderVatNumber")}
                      value={formData.vatNumber}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "none",
                        backgroundColor: "rgba(41, 177, 133, 0.25)",
                      }}
                      required
                    />
                  </div>
                )}
                <div style={{ margin: "20px 0" }}>
                  <h3
                    style={{
                      color: "#29B185",
                      marginBottom: "15px",
                      fontSize: "17px",
                      fontWeight: 600
                    }}
                  >
                    {/* Language */}
                    {t("registration.language")}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "5px",
                      maxWidth: "400px"
                    }}
                  >
                    {[
                      { value: "en", label: "EN" },
                      { value: "de", label: "DE" },
                      { value: "fr", label: "FR" },
                      { value: "it", label: "IT" }
                    ].map((lang) => (
                      <div 
                        key={lang.value}
                        style={{
                          display: "flex",
                          alignItems: "center"
                        }}
                      >
                        <input
                          type="radio"
                          id={`lang-${lang.value}`}
                          name="language"
                          value={lang.value}
                          checked={formData.language === lang.value}
                          onChange={handleChange}
                          style={{
                            appearance: "none",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            border: "2px solid #29B185",
                            marginRight: "8px",
                            position: "relative",
                            cursor: "pointer",
                            outline: "none"
                          }}
                        />
                        <style>
                          {`
                            input[type="radio"]:checked::before {
                              content: '';
                              position: absolute;
                              width: 12px;
                              height: 12px;
                              background: #29B185;
                              border-radius: 50%;
                              top: 50%;
                              left: 50%;
                              transform: translate(-50%, -50%);
                            }
                          `}
                        </style>
                        <label 
                          htmlFor={`lang-${lang.value}`}
                          style={{
                            cursor: "pointer",
                            fontSize: "17px",
                            color: "#333",
                            fontWeight: formData.language === lang.value ? "bold" : "normal"
                          }}
                        >
                          {lang.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <h3
                  style={{
                    color: "#29B185",
                    marginBottom: "10px",
                    marginTop: "20px",
                  }}
                >
                  {t("registration.password")}
                </h3>
                <div style={{ marginBottom: "5px", position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder={t("registration.placeholderPassword")}
                    value={formData.password}
                    onChange={handleChange}
                    style={{
                      width: "100%",
                      padding: "8px 8px 8px 8px",
                      border: "none",
                      backgroundColor: "rgba(41, 177, 133, 0.25)",
                    }}
                    required
                  />

                  {formData.password && (
                    <img
                      src={showPassword ? eyeOffIcon : eyeIcon}
                      alt={t("registration.togglePasswordVisibility")}
                      onClick={() => setShowPassword((prev) => !prev)}
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "10px",
                        transform: "translateY(-50%)",
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        opacity: 0.6,
                      }}
                    />
                  )}
                </div>
                {/* Password validation errors */}
                {passwordErrors.length > 0 && (
                  <div
                    style={{
                      backgroundColor: "#ffebee",
                      border: "1px solid #ef5350",
                      borderRadius: "4px",
                      padding: "10px",
                      marginTop: "5px",
                      marginBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "17px",
                        color: "#c62828",
                        fontWeight: "bold",
                        marginBottom: "5px",
                      }}
                    >
                      {t("registration.passwordErrors.title")}
                    </div>
                    <ul
                      style={{
                        margin: "0",
                        paddingLeft: "20px",
                        fontSize: "17px",
                        color: "#d32f2f",
                      }}
                    >
                      {passwordErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div style={{ margin: "20px 0" }}>
                  <h3
                    style={{
                      color: "#29B185",
                      marginBottom: "15px",
                      fontSize: "17px",
                      fontWeight: 600
                    }}
                  >
                    {t("registration.alert")}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      maxWidth: "400px"
                    }}
                  >
                    {["email", "sms", "app"].map((option) => (
                      <div 
                        key={option}
                        style={{
                          display: "flex",
                          alignItems: "center"
                        }}
                      >
                        <Checkbox2
                          name="alertPreference"
                        value={option}
                        checked={formData.alertPreference.includes(option)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const updated = formData.alertPreference.includes(value)
                            ? formData.alertPreference.filter((v) => v !== value)
                            : [...formData.alertPreference, value];
                          setFormData((prev) => ({
                            ...prev,
                            alertPreference: updated,
                          }));
                        }}
                        label={t(`registration.alertOptions.${option}`)}
                        style={{ whiteSpace: 'nowrap', fontSize: '17px' }}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ 
                  margin: "20px 0",
                  backgroundColor: "rgba(41, 177, 133, 0.25)",
                  padding: "15px",
                  borderRadius: "8px"
                }}>
                  <div style={{ 
                    display: "flex", 
                    alignItems: "flex-start",
                    justifyContent: "center",
                    gap: "10px"
                  }}>
                    <div style={{
                      position: 'relative',
                      width: '18px',
                      height: '18px',
                      minWidth: '18px',
                      border: '1px solid #29B185',
                      borderRadius: '3px',
                      backgroundColor: formData.termsAccepted ? '#29B185' : 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease-in-out',
                      flexShrink: 0,
                      marginTop: '3px'
                    }}>
                      <input
                        type="checkbox"
                        id="termsAccepted"
                        name="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleChange}
                        required
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          margin: 0,
                          cursor: 'pointer'
                        }}
                      />
                      {formData.termsAccepted && (
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            width: '12px',
                            height: '12px',
                            flexShrink: 0
                          }}
                        >
                          <path 
                            d="M20 6L9 17L4 12" 
                            stroke="white" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <div style={{
                      fontSize: "17px",
                      lineHeight: "1.5",
                      color: "#333",
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center"
                    }}>
                      <label 
                        htmlFor="termsAccepted"
                        style={{
                          cursor: "pointer",
                          marginRight: "4px"
                        }}
                      >
                        {t('registration.acceptTerms')}
                      </label>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          window.open("/terms", "_blank", "noopener,noreferrer");
                        }}
                        style={{
                          color: "#29B185",
                          textDecoration: "none",
                          cursor: "pointer",
                          margin: "0 4px"
                        }}
                      >
                        {t('registration.termsAndConditions')}
                      </span>
                      <span style={{ margin: "0 4px" }}>{t('registration.and')}</span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          window.open("/privacy", "_blank", "noopener,noreferrer");
                        }}
                        style={{
                          color: "#29B185",
                          textDecoration: "none",
                          cursor: "pointer",
                          margin: "0 4px"
                        }}
                      >
                        {t('registration.privacyPolicy')}
                      </span>
                    </div>
                    </div>
                  </div>
                </div>{" "}
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "20px",
                    marginBottom: "20px",
                    fontSize: "17px",
                  }}
                >
                  <p>
                    {t("registration.hasAccount")}
                    <button
                      onClick={toggleAuthMode}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#29B185",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "17px",
                      }}
                    >
                       {t("registration.signIn")}
                    </button>
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    backgroundColor: "#29B185",
                    color: "white",
                    marginLeft: "25px",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    fontSize: "22px",
                    fontWeight: "400",
                    width: "90%",
                    marginBottom: "5px",
                  }}
                >
                  {isLoading
                    ? t("registration.registering")
                    : t("registration.registerNow")}
                </button>
              </form>
            </div>
          )
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "50px",
              fontSize: "1.2em",
              color: "#555",
            }}
          >
            {t("dashboard.redirectMessage", {
              userType: currentUser?.userType,
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const style = document.createElement("style");
style.textContent = `
  input {
    font-size: 17px !important;
  }
  input::placeholder {
    color: black !important;
    font-size: 17px !important;
  }
`;
document.head.appendChild(style);

export default Login;
